// Locale → copy for the public "How on-demand FTP works" page, following the site's language like the
// /web-uploader and /preview-first-uploads pages: English (source of truth), Spanish,
// Portuguese, German, French, Italian, Dutch, Norwegian (Bokmål), Japanese, Simplified Chinese, Malay and Thai
// (SEO_LANGS in src/lib/seo/site.ts); any other language falls back to English. A ?lang= the site UI doesn't
// have (fr, nl, no, th) still renders this copy; only the nav and footer stay in English (i18next fallbackLng).
import { useTranslation } from "react-i18next";
import { getApiBaseDomain } from "@/lib/apiDomain";
import { baseLang } from "@/lib/seo/site";
import en, { type OnDemandFtpCopy } from "./copy.en";
import es from "./copy.es";
import pt from "./copy.pt";
import de from "./copy.de";
import fr from "./copy.fr";
import it from "./copy.it";
import nl from "./copy.nl";
import no from "./copy.no";
import ja from "./copy.ja";
import zh from "./copy.zh";
import ms from "./copy.ms";
import th from "./copy.th";

/** Where the page lives on the marketing site. Linked from the "Your FTP server" panel, /web-uploader, the
 *  pricing pages and the shared-FTP reminder emails (photohawk-serverless), so don't change it. */
export const ON_DEMAND_FTP_PATH = "/on-demand-ftp";

/** The page's absolute URL for the current environment (photohawk.com or photohawk-staging.com), for links from
 *  the uploader, which also runs on upload.<domain> and on tenant hosts. */
export const onDemandFtpPageUrl = () => `https://${getApiBaseDomain()}${ON_DEMAND_FTP_PATH}`;

/** The web uploader with "Your FTP server" open (?ftp=1, UploaderExplorer). */
export const FTP_START_URL = "https://upload.photohawk.com/?ftp=1";

const COPY_BY_LANG: Record<string, OnDemandFtpCopy> = { en, es, pt, de, fr, it, nl, no, ja, zh, ms, th };

export const getOnDemandFtpCopy = (lang: string | undefined): OnDemandFtpCopy => COPY_BY_LANG[baseLang(lang)] ?? en;

export function useOnDemandFtpCopy(): { copy: OnDemandFtpCopy; lang: string } {
  const { i18n } = useTranslation();
  const base = baseLang(i18n.language);
  const lang = COPY_BY_LANG[base] ? base : "en";
  return { copy: COPY_BY_LANG[lang], lang };
}

export type { OnDemandFtpCopy };
